<script setup lang="ts">
import VitaInput from '@/components/UI/VitaInput.vue';
import { ComponentPublicInstance, computed, nextTick, Ref, ref, watch } from 'vue';
import store from '@/store';
import { ExternalUser, NewUser } from '@/types';
import { checkUnsavedChanges, clearNull } from '@/utils/main';
import { reachGoal } from '@/utils/metrika';

const userSearch = ref('')
const userSearchDrop = ref(false)
const inProcess = ref(false)
const currentPharmID = ref(0)
const searchDropRef: Ref<HTMLElement | ComponentPublicInstance | null> = ref(null)
const userSearchInput = ref<HTMLInputElement | null>(null)

const users = computed<NewUser[]>(() => {
  return store.getters["data/pharmUsers"];
});

// Есть ли у пользователя роль OFFLINE_DIRECTOR
const isOfflineDirectorRole = computed<boolean>(() => {
  return store.getters['getOfflineDirectorRole']
})

const searchUsersCount = computed(() => {
  return store.getters["data/newSearchUsersCount"];
});

const searchUsersList = computed<ExternalUser[]>(() => {
  return store.getters["data/newSearchUserList"];
});

const searchUsersListSkip = computed(() => {
  return store.getters["data/newSearchUsersSkip"];
});

const pharmId = computed(() => {
  return store.getters['data/pharmId']
});

// Есть ли у пользователя роль WFM_READER
const isWFMReaderRole = computed<boolean>(() => {
  return store.getters['getWFMReaderRole']
})

const hasArrows = computed(() => searchUsersCount.value > 5);
const noResults = computed(() => searchUsersList.value && searchUsersList.value.length === 0);
const hasPrevArrow = computed(() => searchUsersList.value.length > 0 && searchUsersListSkip.value > 0);
const hasNextArrow = computed(() =>
  searchUsersList.value.length > 0 && searchUsersCount.value > searchUsersListSkip.value + 5
);

const dropdownClasses = computed(() => ({
  'at-top': users.value.length < 3,
  'in-process': noResults.value ||
    (inProcess.value && !searchUsersList.value) ||
    (searchUsersList.value && searchUsersList.value.length < 2)
}));

const addNewUser = async (user: ExternalUser) => {
  userSearchDrop.value = false;

  // noinspection ES6RedundantAwait
  await store.commit('data/addUser', user);
  await store.dispatch("data/getTimeSchedule");

  checkUnsavedChanges()
  reachGoal('timetable-employee-add-selected')
}

const closeSearchDrop = () => {
  userSearchDrop.value = false
}

const openSearchDrop = (event: MouseEvent) => {
  if ((event.target === document.activeElement) && userSearchDrop.value) {
    return
  }

  if (currentPharmID.value === 0 || currentPharmID.value !== pharmId.value || searchUsersList.value?.length === 0) {
    searchUser();
  }

  userSearchDrop.value = true

  if (userSearch.value.length > 0) {
    userSearchDrop.value = true
  }
  reachGoal('timetable-employee-add-click')
}

const onSearchInput = () => {
  inProcess.value = true
  if (searchUsersListSkip.value > 0) {
    store.commit('data/setUserSearchSkip', 0)
  }
}

const searchUser = async () => {
  inProcess.value = true

  try {
    await store.dispatch('data/getPharmPeopleList', userSearch.value);
  } catch {
    if (localStorage.getItem('token')) console.error('Ошибка при поиске');
  } finally {
    inProcess.value = false
  }
}

const switchPage = async (direction: 'prev' | 'next') => {
  inProcess.value = true

  if (direction === 'next') {
    store.commit('data/increaseUserSearchQuery')
  } else {
    store.commit('data/decreaseUserSearchQuery')
  }
  await store.dispatch("data/getPharmPeopleList", userSearch.value);

  // Возвращаем scroll вверх при переключении страниц
  await nextTick(() => {
    const el = (searchDropRef.value as ComponentPublicInstance)?.$el || searchDropRef.value;
    if (el instanceof HTMLElement && el.scrollTop > 0) {
      el.scrollTop = 0;
    }
  });
  inProcess.value = false
}

const scrollOptions = { wheelPropagation: false, suppressScrollX: true }

const getFullName = (user: ExternalUser) => {
  const {lastName, firstName, middleName} = user
  return `${clearNull(lastName)} ${clearNull(firstName)} ${clearNull(middleName)}`;
}

// Задержка для запросов при вводе пользователя
let debounceTimer: ReturnType<typeof setTimeout>;

watch(userSearch, () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    searchUser();
  }, 500);
});
</script>

<template>
  <div class="employees__add" v-if="pharmId && !isWFMReaderRole && !isOfflineDirectorRole">
    <div class="employees__add-title">
      <vita-icon name="external-user"/>
      <span>Добавить сотрудника</span>
    </div>
    <div class="list-res-wrap" v-click-outside="() => closeSearchDrop()">
      <vita-input
        v-model="userSearch"
        ref="userSearchInput"
        type="text"
        icon="search"
        placeholder="Сотрудник"
        :oninput="onSearchInput"
        @click="openSearchDrop($event)"
        data-qa="employee-add-input-search"
      />
      <Transition name="fade-ease">
      <div class="list-res-drop"
           :class="dropdownClasses"
           v-if="userSearchDrop"
      >
        <div v-if="searchUsersList && searchUsersList.length > 0">
          <perfect-scrollbar class="list-res-scroll rail-thin" :options="scrollOptions" ref="searchDropRef">
            <div class="list-res">
              <div class="list-res-item"
                   :class="{'disabled' : inProcess}"
                   v-for="user in searchUsersList"
                   :key="user.zupGuid"
                   @click="addNewUser(user)"
                   data-qa="employee-add-option-select"
              >
                <div class="list-res-title">{{getFullName(user)}}</div>
                <div class="list-res-details">
                  <div :title="user.position">{{user.position}} ({{user.bid}})</div>
                  <div v-if="user.separator">{{user.separator}}</div>
                </div>
              </div>
            </div>
          </perfect-scrollbar>
          <div class="arrows__pagination" v-if="hasArrows && (hasPrevArrow || hasNextArrow)">
            <div>
              <button class="arrows__pagination-item" type="button" @click="switchPage('prev')" v-if="hasPrevArrow" :disabled="inProcess"
                      data-qa="employee-add-button-page-prev">
                <vita-icon name="circle-arrow-left"/>
              </button>
            </div>
            <div>
              <button class="arrows__pagination-item" type="button" @click="switchPage('next')" v-if="hasNextArrow" :disabled="inProcess"
                      data-qa="employee-add-button-page-next">
                <vita-icon name="circle-arrow-right"/>
              </button>
            </div>
          </div>
        </div>
        <div class="list-res-empty" v-else-if="inProcess">Загрузка</div>
        <div class="list-res-empty" v-else>Результатов не найдено</div>
      </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "../static/style/variables.scss" as *;
@use "../static/style/mixins.scss" as *;

.list-res-wrap {
  position: relative;
}

.list-res-drop {
  position: absolute;
  bottom: -15px;
  left: calc(100% + 27px);
  z-index: 1001;
  background: #FFFFFF;
  border-radius: 3px;
  padding: 12px 0;
  border: 1px solid rgba(229, 229, 229, 1);
  box-shadow: -1px 0px 12px 0px rgba(0, 0, 0, 0.15);
  min-width: 301px;
  margin-bottom: 2px;

  &::before {
    content: '';
    position: absolute;
    bottom: 14px;
    left: -28px;
    width: 30px;
    height: 36px;
    background-image: url("data:image/svg+xml,%3Csvg width='28' height='37' viewBox='0 0 28 37' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M28 0.333496V36.3335L2.28746 20.906C0.345448 19.7408 0.345448 16.9262 2.28746 15.761L28 0.333496Z' fill='white'/%3E%3C/svg%3E%0A");
  }

  &.at-top {
    bottom: unset;
    top: 0;
    transform: translateY(-48px);

    &::before {
      top: 0;
      transform: translateY(48px);
      bottom: unset;
    }

    &.in-process {
      transform: translateY(-13px);

      &::before {
        top: 0;
        transform: translateY(13px);
        bottom: unset;
      }
    }
  }

  .list-res-scroll {
    max-height: 244px;
  }
}

.list-res {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &-item {
    cursor: pointer;
    padding-left: 8px;
    padding-right: 8px;
    transition: opacity .3s;

    &.disabled {
      opacity: .5;
      pointer-events: none;
    }
  }

  &-title {
    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    color: #333333;
  }

  &-details {
    display: flex;
    justify-content: space-between;
    gap: 6px;
    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    color: rgba(51, 51, 51, 0.6);
    margin-top: 4px;

    > :first-child {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    > :last-child {
      white-space: nowrap;
    }
  }

  &-empty {
    font-style: normal;
    font-weight: 400;
    font-size: 11px;
    line-height: 13px;
    text-align: center;
    color: #BBBBBB;
    padding: 13px 0;
  }

  &-scroll {
    overflow: hidden;
  }
}

.employees__add {
  background: $bg-color-light;
  border-radius: 0 3px 3px 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #CCCCCC;
  height: auto;
  justify-content: flex-start;
  padding: 13px 12px;
  margin-bottom: 0;

  &-title {
    color: rgba(255, 111, 32, 1);
    display: flex;
    align-items: center;
    gap: 8px;

    &:not(:last-child) {
      margin-bottom: 10px;
    }

    span {
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 15px;
    }
  }

  .input-box {
    min-height: 38px;
    margin-bottom: 0;
  }
}

.arrows__pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 8px 0;

  > div {
    display: flex;
  }

  &-item {
    background: unset;
    border: 0;
    width: 15px;
    height: 15px;
    cursor: pointer;
    transition: opacity .3s;

    &[disabled] {
      opacity: .5;
      pointer-events: none;
    }
  }
}
</style>